import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface BadgeData {
    [key: string]: string; // ex: { visitorName: 'John', company: 'Acme', photo: 'data:image/...' }
}

@Component({
    selector: 'app-badge-renderer',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './badge-render.component.html'
})
export class BadgeRendererComponent implements OnChanges {

    @Input() template: string = '';       // HTML template avec placeholders {{key}}
    @Input() data: BadgeData = {};        // Données à injecter
    @Input() width: number = 400;        // Largeur badge en px
    @Input() height: number = 250;       // Hauteur badge en px
    @Input() autoRender: boolean = true;  // Render automatiquement au changement

    @Output() imageGenerated = new EventEmitter<string>();   // data URL base64
    @Output() imageBlob = new EventEmitter<Blob>();          // Blob pour upload/print

    @ViewChild('badgeCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

    renderedHtml: string = '';
    generatedImage: string | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (this.autoRender && (changes['template'] || changes['data'])) {
            setTimeout(() => this.renderBadge(), 0);
        }
    }

    /**
     * Résout un chemin imbriqué: "visitor.fullname" → data.visitor.fullname
     */
    private resolveNestedPath(data: any, path: string): string {
        const value = path.split('.').reduce((obj, key) => obj?.[key], data);
        return value != null ? String(value) : '';
    }

    /**
 * Remplace les placeholders {{key}} par les valeurs de data.
 * Supporte les chemins imbriqués: {{visitor.fullname}}
 * Pour les images: {{img:key}} est remplacé par un <img src="...">
 */
    private interpolate(template: string, data: BadgeData): string {
        let result = template;

        // Remplacement des images: {{img:photo}} ou {{img:visitor.photo}}
        result = result.replace(/\{\{img:([\w.]+)\}\}/g, (_, path) => {
            const src = this.resolveNestedPath(data, path);
            return src ? `<img src="${src}" style="max-width:100%;max-height:100%;" />` : '';
        });

        // Remplacement des textes: {{visitor.fullname}} ou {{company}}
        result = result.replace(/\{\{([\w.]+)\}\}/g, (_, path) => {
            return this.resolveNestedPath(data, path);
        });

        return result;
    }

    async renderBadge(): Promise<string | null> {
        if (!this.template) return null;

        const canvas = this.canvasRef?.nativeElement;
        if (!canvas) return null;
        // canvas.width = this.width;
        // canvas.height = this.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        // ctx.clearRect(0, 0, this.width, this.height);

        // --- Étape 1 : Séparer images et texte ---
        const imageSlots: { id: string; src: string }[] = [];
        let slotIndex = 0;

        // Cas 1 : <img src="{{img:key}}" .../> → remplacer TOUTE la balise <img>
        let htmlForSvg = this.template.replace(/<img([^>]*)src=["']?\{\{img:([\w.]+)\}\}["']?([^>]*)\/?>/gi, (fullMatch, _before, path) => {
            const src = this.resolveNestedPath(this.data, path);
            const id = `img-slot-${slotIndex++}`;
            if (src) {
                imageSlots.push({ id, src });
            }
            // Extraire le style original de l'img
            const styleMatch = fullMatch.match(/style=["']([^"']*)["']/i);
            const style = styleMatch ? styleMatch[1] : 'width:100%;min-height:80px;';
            return `<div data-img-slot="${id}" style="display:block;${style};background:#f0f0f0;"></div>`;
        });

        // Cas 2 : {{img:key}} standalone
        htmlForSvg = htmlForSvg.replace(/\{\{img:([\w.]+)\}\}/g, (_, path) => {
            const src = this.resolveNestedPath(this.data, path);
            const id = `img-slot-${slotIndex++}`;
            if (src) {
                imageSlots.push({ id, src });
            }
            return `<div data-img-slot="${id}" style="display:block;width:100%;min-height:80px;background:#f0f0f0;"></div>`;
        });

        // Remplacer les textes {{key}}
        htmlForSvg = htmlForSvg.replace(/\{\{([\w.]+)\}\}/g, (_, path) => {
            return this.resolveNestedPath(this.data, path);
        });

        // --- Auto-détection des dimensions du contenu ---
        const sizer = document.createElement('div');
        sizer.style.cssText = 'position:absolute;left:-9999px;top:-9999px;display:inline-block;';
        sizer.innerHTML = htmlForSvg;
        document.body.appendChild(sizer);
        const renderWidth = Math.max(sizer.scrollWidth, this.width);
        const renderHeight = Math.max(sizer.scrollHeight, this.height);
        document.body.removeChild(sizer);

        canvas.width = renderWidth;
        canvas.height = renderHeight;
        ctx.clearRect(0, 0, renderWidth, renderHeight);

        // --- Étape 2 : Rendre le SVG (texte seulement) sur le canvas ---
        // Sanitize HTML → XHTML valide pour SVG
        const sanitizer = document.createElement('div');
        sanitizer.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
        sanitizer.style.cssText = `width:${renderWidth}px;height:${renderHeight}px;overflow:hidden;font-family:Arial,sans-serif;`;
        sanitizer.innerHTML = htmlForSvg;
        const xhtml = new XMLSerializer().serializeToString(sanitizer);

        const svgHtml = `
            <svg xmlns="http://www.w3.org/2000/svg" width="${renderWidth}" height="${renderHeight}">
                <foreignObject width="100%" height="100%">
                    ${xhtml}
                </foreignObject>
            </svg>`;

        const svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgHtml);
        const svgImg = new Image();

        await new Promise<void>((resolve, reject) => {
            svgImg.onload = () => resolve();
            svgImg.onerror = (e) => {
                console.error('Error loading SVG:', e);
                return reject(e)
            };
            svgImg.src = svgUrl;
        });

        ctx.drawImage(svgImg, 0, 0);

        // --- Étape 3 : Dessiner les images par-dessus via un div de mesure ---
        if (imageSlots.length > 0) {
            const measurer = document.createElement('div');
            measurer.style.cssText = `position:absolute;left:-9999px;top:-9999px;width:${renderWidth}px;height:${renderHeight}px;overflow:hidden;font-family:Arial,sans-serif;`;
            measurer.innerHTML = htmlForSvg;
            document.body.appendChild(measurer);

            for (const slot of imageSlots) {
                const slotEl = measurer.querySelector(`[data-img-slot="${slot.id}"]`) as HTMLElement;
                if (!slotEl) continue;

                const parentRect = measurer.getBoundingClientRect();
                const rect = slotEl.getBoundingClientRect();
                const x = rect.left - parentRect.left;
                const y = rect.top - parentRect.top;
                const w = rect.width;
                const h = rect.height;

                // Charger l'image
                const img = new Image();
                await new Promise<void>((resolve) => {
                    img.onload = () => resolve();
                    img.onerror = () => resolve(); // continuer même si erreur
                    img.src = slot.src;
                });

                if (img.complete && img.naturalWidth > 0) {
                    let drawW = w > 0 ? w : Math.min(img.naturalWidth, renderWidth);
                    let drawH = h > 0 ? h : Math.min(img.naturalHeight, renderHeight);
                    const scale = Math.min(drawW / img.naturalWidth, drawH / img.naturalHeight);
                    const dw = img.naturalWidth * scale;
                    const dh = img.naturalHeight * scale;
                    const dx = x + (drawW - dw) / 2;
                    const dy = y + (drawH - dh) / 2;
                    ctx.drawImage(img, dx, dy, dw, dh);
                }
            }

            document.body.removeChild(measurer);
        }

        // --- Étape 4 : Exporter ---
        const dataUrl = canvas.toDataURL('image/png');
        this.generatedImage = dataUrl;
        this.imageGenerated.emit(dataUrl);

        canvas.toBlob((blob) => {
            if (blob) this.imageBlob.emit(blob);
        }, 'image/png');

        return dataUrl;
    }

    // async renderBadge(): Promise<string | null> {
    //     if (!this.template) return null;

    //     this.renderedHtml = this.interpolate(this.template, this.data);

    //     // Wrapper le HTML dans un foreignObject SVG
    //     const svgHtml = `
    //   <svg xmlns="http://www.w3.org/2000/svg" width="${this.width}" height="${this.height}">
    //     <foreignObject width="100%" height="100%">
    //       <div xmlns="http://www.w3.org/1999/xhtml" 
    //            style="width:${this.width}px;height:${this.height}px;overflow:hidden;font-family:Arial,sans-serif;">
    //         ${this.renderedHtml}
    //       </div>
    //     </foreignObject>
    //   </svg>
    // `;

    //     // data URI au lieu de blob URL pour éviter le tainted canvas
    //     const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgHtml);

    //     const img = new Image();
    //     img.width = this.width;
    //     img.height = this.height;

    //     await new Promise<void>((resolve, reject) => {
    //         img.onload = () => resolve();
    //         img.onerror = (e) => reject(e);
    //         img.src = url;
    //     });

    //     const canvas = this.canvasRef?.nativeElement;
    //     if (!canvas) return null;

    //     canvas.width = this.width;
    //     canvas.height = this.height;
    //     const ctx = canvas.getContext('2d');
    //     if (!ctx) return null;

    //     ctx.clearRect(0, 0, this.width, this.height);
    //     ctx.drawImage(img, 0, 0);

    //     const dataUrl = canvas.toDataURL('image/png');
    //     this.generatedImage = dataUrl;
    //     this.imageGenerated.emit(dataUrl);

    //     canvas.toBlob((blob) => {
    //         if (blob) this.imageBlob.emit(blob);
    //     }, 'image/png');

    //     return dataUrl;
    // }
}